import React, { PropsWithChildren, Component, ErrorInfo } from 'react';
import {
  datadogRum,
  RumActionEvent,
  RumEvent,
  RumErrorEvent,
  RumResourceEvent,
  RumFetchResourceEventDomainContext,
  RumXhrResourceEventDomainContext,
  RumOtherResourceEventDomainContext,
  RumViewEvent,
  RumLongTaskEvent,
} from '@datadog/browser-rum';

import { config } from '../config';

// ===== Constants & Types =====
// Environment check to disable error reporting in development
const isDevelopment = import.meta.env.MODE === 'development';
// Keyword to identify guest-widgets related errors in stack traces
const KEYWORD = 'guest';

// Simple error suppressor
const ERROR_DEBOUNCE_TIME = 5000; // 5 seconds

const getErrorTracker = (): Map<string, number> => {
  try {
    const stored = sessionStorage.getItem('guest-widgets-error-tracker');
    if (stored) {
      return new Map(JSON.parse(stored));
    }
  } catch (e) {
    console.warn('[guest-widgets] Failed to load error tracker:', e);
  }
  return new Map();
};

const saveErrorTracker = (tracker: Map<string, number>) => {
  try {
    sessionStorage.setItem(
      'guest-widgets-error-tracker',
      JSON.stringify(Array.from(tracker.entries()))
    );
  } catch (e) {
    console.warn('[guest-widgets] Failed to save error tracker:', e);
  }
};

const shouldSuppressError = (errorKey: string): boolean => {
  const now = Date.now();
  const errorTracker = getErrorTracker();
  const lastReported = errorTracker.get(errorKey);

  // If no previous report or more than 5 seconds have passed, allow the error
  if (!lastReported || now - lastReported > ERROR_DEBOUNCE_TIME) {
    errorTracker.set(errorKey, now);
    saveErrorTracker(errorTracker);
    return false;
  }

  return true;
};

// State interface for the ErrorBoundary component
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

// Extended XHR interface to store request metadata
interface ExtendedXMLHttpRequest extends XMLHttpRequest {
  _url?: string;
  _method?: string;
}

// Union type for different RUM event contexts
type RumSourceContext =
  | RumFetchResourceEventDomainContext
  | RumXhrResourceEventDomainContext
  | RumOtherResourceEventDomainContext;

// ===== Error Handling =====
/**
 * Creates a standardized error with guest-widgets prefix and preserves original stack trace
 * @param message - Error message to display
 * @param originalError - Optional original error to preserve stack trace
 */
const createGuestError = (message: string, originalError?: Error): Error => {
  const error = new Error(`[guest-widgets] ${message}`);
  error.stack = originalError?.stack || new Error().stack;
  return error;
};

/**
 * Reports React component errors to Datadog RUM
 * Creates a view and adds error with context
 */
const reportToDatadog = (error: Error, context: Record<string, unknown> = {}) => {
  if (isDevelopment) return;

  const errorKey = `react-${error.message}`;
  if (shouldSuppressError(errorKey)) {
    return;
  }

  datadogRum.startView({
    name: 'guest-widgets-error',
    service: 'guest-widgets',
    version: config.apmVersion,
  });
  datadogRum.addError(error, { source: 'react', component: 'guest-widgets', ...context });
};

/**
 * Handles network errors from fetch API
 * Creates standardized error and reports to Datadog
 */
const handleNetworkError = (
  error: unknown,
  request: RequestInfo | URL,
  type: 'fetch' | 'xhr' = 'fetch'
) => {
  const url = request instanceof Request ? request.url : String(request);
  const method = request instanceof Request ? request.method : 'GET';
  const errorMessage = error instanceof Error ? error.message : String(error);
  const guestError = createGuestError(`Network error: ${method} ${url} - ${errorMessage}`);

  const errorKey = `${type}-${method}-${url}`;
  if (shouldSuppressError(errorKey)) {
    return guestError;
  }

  if (!isDevelopment) {
    datadogRum.startView({
      name: `guest-widgets-${type}-error`,
      service: 'guest-widgets',
      version: config.apmVersion,
    });
    datadogRum.addError(guestError, {
      source: type,
      url,
      method,
      errorType: error instanceof Error ? error.name : 'Unknown',
      errorMessage,
    });
  }

  return guestError;
};

/**
 * Handles XHR-specific errors
 * Captures XHR request details and reports to Datadog
 */
const handleXHRError = (xhr: ExtendedXMLHttpRequest, error: Event | null = null) => {
  const url = xhr._url || 'unknown';
  const method = xhr._method || 'GET';
  const errorMessage = error
    ? `Network error: ${error.type}`
    : `HTTP error: ${xhr.status} ${xhr.statusText}`;
  const guestError = createGuestError(`${method} ${url} - ${errorMessage}`);

  const errorKey = `xhr-${method}-${url}`;
  if (shouldSuppressError(errorKey)) {
    return guestError;
  }

  if (!isDevelopment) {
    datadogRum.startView({
      name: 'guest-widgets-xhr-error',
      service: 'guest-widgets',
      version: config.apmVersion,
    });
    datadogRum.addError(guestError, {
      source: 'xhr',
      url,
      method,
      ...(error
        ? { errorType: error.type }
        : { status: xhr.status, statusText: xhr.statusText, responseText: xhr.responseText }),
    });
  }

  return guestError;
};

// ===== React Error Boundary =====
/**
 * React Error Boundary component that catches and reports errors in child components
 * Uses componentDidCatch to report errors to Datadog
 */
export class ErrorBoundaryWrapper extends Component<PropsWithChildren<{}>, ErrorBoundaryState> {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const guestError = createGuestError(error.message, error);
    reportToDatadog(guestError, { componentStack: errorInfo.componentStack });
  }

  render() {
    return this.props.children;
  }
}

// ===== Type Guards =====
/**
 * Generic type guard factory for RUM events
 * Creates type guards for different event types
 */
const isEventType = <T extends RumEvent>(type: string) => (event: RumEvent): event is T =>
  (event as T).type === type;

// Type guards for different RUM event types
const isResourceEvent = isEventType<RumResourceEvent>('resource');
const isActionEvent = isEventType<RumActionEvent>('action');
const isErrorEvent = isEventType<RumErrorEvent>('error');
const isViewEvent = isEventType<RumViewEvent>('view');
const isLongTaskEvent = isEventType<RumLongTaskEvent>('long_task');

// ===== Datadog RUM Setup =====
/**
 * Initializes Datadog RUM and sets up error tracking
 * - Configures fetch error handling
 * - Configures XHR error handling
 * - Initializes Datadog RUM with custom configuration
 * - Sets up global error handlers
 */
export const APMInit = () => {
  if (isDevelopment) return;

  // Setup fetch error handling
  const originalFetch = window.fetch;
  window.fetch = async (...args) => {
    try {
      const response = await originalFetch(...args);
      return response.ok
        ? response
        : Promise.reject(
            handleNetworkError(new Error(`${response.status} ${response.statusText}`), args[0])
          );
    } catch (error) {
      throw handleNetworkError(error, args[0]);
    }
  };

  // Setup XHR error handling
  const originalXHROpen = XMLHttpRequest.prototype.open;
  const originalXHRSend = XMLHttpRequest.prototype.send;

  // Override XHR open to capture request metadata
  XMLHttpRequest.prototype.open = function (
    method: string,
    url: string | URL,
    async: boolean = true,
    username?: string | null,
    password?: string | null
  ) {
    (this as ExtendedXMLHttpRequest)._url = String(url);
    (this as ExtendedXMLHttpRequest)._method = method;
    return originalXHROpen.call(this, method, url, async, username, password);
  };

  // Override XHR send to add error handlers
  XMLHttpRequest.prototype.send = function (...args) {
    this.addEventListener('error', (event) =>
      handleXHRError(this as ExtendedXMLHttpRequest, event)
    );
    this.addEventListener(
      'load',
      () => this.status >= 400 && handleXHRError(this as ExtendedXMLHttpRequest)
    );
    return originalXHRSend.apply(this, args);
  };

  // Initialize Datadog RUM with custom configuration
  datadogRum.init({
    applicationId: 'f8481b95-2cc5-41f8-b6f2-d5ea28fc305c',
    clientToken: 'pub593baf37bf3257b57e484f79fbe9e82e',
    site: 'datadoghq.eu',
    service: 'guest-widgets',
    env: config.env,
    version: config.apmVersion,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true,
    trackViewsManually: true,
    // Filter events to only include guest-widgets related ones
    beforeSend: (event, context) => {
      if (isActionEvent(event)) return event.action.type === 'custom';
      if (isResourceEvent(event))
        return (
          !!(context as RumSourceContext).performanceEntry?.name?.includes(KEYWORD) ||
          !!(context as RumXhrResourceEventDomainContext).xhr?.responseURL?.includes(KEYWORD) ||
          !!(context as RumFetchResourceEventDomainContext).response?.url?.includes(KEYWORD)
        );
      if (isErrorEvent(event))
        return (
          !!event.error.stack?.includes(KEYWORD) || !!event.error.handling_stack?.includes(KEYWORD)
        );
      if (isViewEvent(event)) return true;
      if (isLongTaskEvent(event)) return false;
      return false;
    },
    allowedTracingUrls: [(url) => url.includes(KEYWORD)],
  });
};
